import { useCallback, useMemo } from 'react';
import * as turf from '@turf/turf';
import { Units } from '@turf/turf';
import { MapRef } from 'react-map-gl/maplibre';

import { defaultItalyCoordinates } from '@restworld/utils-common';

// Custom hook to calculate the bounding radius of a map view. It lives in the worker app, its only
// user, rather than in utils-common: through that barrel turf reached every page of every app
// (RW-235, item 4).
export const useMapBoundingRadius = ({ map }: { map?: MapRef | null }) => {
  // Memoize the map center coordinates, fallback to default if unavailable
  const center = useMemo(
    () => (map?.getCenter() ? [map.getCenter().lng, map.getCenter().lat] : defaultItalyCoordinates),
    [map]
  );

  // Get the northeast corner of the current map bounds
  const getBoundsCorner = useCallback(() => {
    const b = map?.getBounds();
    if (b) {
      // Get the northeast corner
      const ne = b.getNorthEast();
      return [ne.lng, ne.lat];
    }
    return null;
  }, [map]);

  // Calculate the radius from a given point to the bounds corner, doubled for full coverage
  const getRadius = useCallback(
    ({ lon, lat }: { lon: number; lat: number }) => {
      const boundsCorner = getBoundsCorner();
      if (!boundsCorner) return;
      // Create turf points for distance calculation
      const from = turf.point([lon, lat]);
      const to = turf.point(boundsCorner);
      const options = { units: 'meters' as Units };
      // Calculate the distance between the center and the bounds corner
      const distance = turf.distance(from, to, options);
      // Ensure a minimum radius (e.g., 500 meters)
      return Math.max(distance * 2, 500);
    },
    [getBoundsCorner]
  );

  // Return the radius calculation function and the map center
  return { getRadius, center };
};
